/**
 * Fraction of the *scrollport* that must show the outro to unveil.
 * (Not a fraction of the outro band — a short Abschluss on a tall page
 * never fills the viewport, so a target-relative IO threshold never fires.)
 */
const OUTRO_REVEAL_COVER_RATIO = 0.35;

/**
 * Once unveiled, stay unveiled until coverage falls below this.
 * A single threshold flaps on a 4K scrollport: subpixel scroll offsets
 * sit on the boundary and the blur / TOC highlight toggle every frame.
 */
const OUTRO_HIDE_COVER_RATIO = 0.2;

/** How close to the bottom (px) counts as scroll-end when still veiled. */
const SCROLL_END_REVEAL_SLACK_PX = 8;

/**
 * Once unveiled via scroll-end, ignore a few extra pixels of bounce.
 * Trackpad frames on a tall 4K page move `scrollTop + clientHeight`
 * around `scrollHeight` by more than a couple of pixels.
 */
const SCROLL_END_HIDE_SLACK_PX = 48;

/**
 * Whether Abschluss should unveil for the current scroll geometry.
 * Viewport coverage ignores outro band height; scroll-end covers the case
 * where only a peek of a tall band is reachable.
 *
 * `currentlyActive` picks the looser threshold so a boundary wobble does
 * not veil and unveil on alternating frames.
 */
const isOutroBandRevealed = (
  outro: HTMLElement,
  scrollRoot: HTMLElement,
  currentlyActive = false
): boolean => {
  const rootRect = scrollRoot.getBoundingClientRect();
  const outroRect = outro.getBoundingClientRect();
  if (rootRect.height <= 0) return false;

  const visibleTop = Math.max(outroRect.top, rootRect.top);
  const visibleBottom = Math.min(outroRect.bottom, rootRect.bottom);
  const visibleHeight = Math.max(0, visibleBottom - visibleTop);
  if (visibleHeight <= 0) return false;

  const coverRatio = currentlyActive
    ? OUTRO_HIDE_COVER_RATIO
    : OUTRO_REVEAL_COVER_RATIO;

  if (visibleHeight / rootRect.height >= coverRatio) {
    return true;
  }

  const distanceFromEnd =
    scrollRoot.scrollHeight - scrollRoot.clientHeight - scrollRoot.scrollTop;
  const endSlack = currentlyActive
    ? SCROLL_END_HIDE_SLACK_PX
    : SCROLL_END_REVEAL_SLACK_PX;

  return distanceFromEnd <= endSlack;
};

export { isOutroBandRevealed };
